/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V23.0 - Data Router Migration) ---
 * - MISSION: To enable advanced React Router features (specifically `useBlocker` for upload safety)
 *   by migrating from `<BrowserRouter>` to `createBrowserRouter`.
 *
 * - ARCHITECTURE:
 *   1. `AppProviders`: A pure wrapper component for all Context Providers. NO router hooks allowed here.
 *   2. `AppShell`: The internal application shell that sits INSIDE the Router context.
 *      It safely uses hooks like `useAnalytics`, `useNavigate`, `useLocation` and renders `Outlet`.
 *   3. `router`: Defined via `createBrowserRouter`, wrapping the root route in `<AppProviders><AppShell /></AppProviders>`.
 *
 * - RESULT: This definitive fix eliminates "useContext out of Provider" errors while unlocking
 *   robust navigation blocking for file uploads.
 */

import { useReferralAttribution } from '@/hooks/useReferralAttribution';
import { Suspense, lazy, useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Route,
  Outlet,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { redeemResumeToken } from '@/services/api';
import { Toaster } from 'react-hot-toast'; // --- NEW: Global Toaster

import { ModalProvider, useModal } from './context/ModalContext';
import CarouselProvider from './context/CarouselProvider';
import { WalletProvider } from './context/WalletContext';
import { SocialAuthProvider } from './context/SocialAuthProvider';
import { NavigationProvider } from './context/NavigationProvider';
import { AnalyticsProvider } from './context/AnalyticsProvider';
import { UserProvider } from './context/UserProvider';
import { GuestUserProvider } from './context/GuestUserContext';
import { LoadingProvider } from './context/LoadingProvider';
import { useLoading } from './hooks/useLoading';
import { useAnalytics } from './hooks/useAnalytics';
import { ExitIntentDetector } from '@/components/shared/ExitIntentDetector';

import AuthLayout from './components/layout/AuthLayout';
import CookieBanner from './components/layout/CookieBanner';
import AnalyticsTracker from './components/AnalyticsTracker';
import ModalLoading from './components/shared/ModalLoading';
import SuspenseManager from './components/layout/SuspenseManager';
import ProtectedRoute from './components/shared/ProtectedRoute';
import ScrollToTop from './components/shared/ScrollToTop';
import MainLayout from './components/layout/MainLayout';
import ResumePrompt from './components/shared/ResumePrompt';
import GlobalErrorBoundary from './components/shared/GlobalErrorBoundary';
import Home from './pages/Home';
import { isTVDevice } from './utils/tvDetect';
import { loadSpatialNav } from './utils/spatialNav';

if (isTVDevice()) {
  loadSpatialNav().then(({ init }) => {
    init({
      selector: 'button, a, input, [tabindex="0"]',
      defaultElement: '#root'
    });
  });
}

const Home2 = lazy(() => import('./pages/Home2'));
const TVActivatePage = lazy(() => import('./pages/TVActivatePage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const GlobalAiButton = lazy(() => import('./components/shared/GlobalAiButton'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Redeem = lazy(() => import('./pages/Redeem'));
const FAQ = lazy(() => import('./pages/FAQ'));
const EarnPage = lazy(() => import('./pages/EarnPage'));
const EarnDashboard = lazy(() => import('./pages/EarnDashboard'));
const ReferralRedirect = lazy(() => import('./pages/ReferralRedirect'));
const AffiliateTermsPage = lazy(() => import('./pages/AffiliateTermsPage'));
const AffiliateAdmin = lazy(() => import('./pages/AffiliateAdmin'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const Settings = lazy(() => import('./pages/Settings'));
const IndikinPassStatus = lazy(() => import('./pages/IndikinPassStatus'));
const PublicSharePage = lazy(() => import('./pages/PublicSharePage'));
const AuthActionHandler = lazy(() => import('./pages/AuthActionHandler'));
const ClaimAccountPage = lazy(() => import('./pages/ClaimAccountPage'));
const StorymakerTermsPage = lazy(() => import('./pages/StorymakerTermsPage'));

const DashboardLayoutWrapper = lazy(() => import('./components/layout/DashboardLayoutWrapper'));

// --- AppEffects Component (Internal) ---
const AppEffects = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal } = useModal();
  
  useReferralAttribution();

  useEffect(() => {
    const handleResumption = async () => {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('resume_token');
      const modalParam = searchParams.get('modal');

      if (token) {
        try {
          const data = await redeemResumeToken(token);
          if (data && data.tier) {
            const tier = {
              id: data.tier.id,
              title: data.tier.name,
              price: data.resumptionData.totalAmount
            };
            openModal('payment', {
              tier,
              initialTab: 'crypto',
              resumptionData: data.resumptionData,
            });
          }
        } catch (error) {
          console.error('Failed to redeem resume token:', error);
        } finally {
          searchParams.delete('resume_token');
          navigate({ search: searchParams.toString() }, { replace: true });
        }
      }

      if (modalParam === 'litepaper') {
        openModal('litepaper');
        searchParams.delete('modal');
        navigate({ search: searchParams.toString() }, { replace: true });
      }
    };
    handleResumption();
  }, [location.search, navigate, openModal]);

  useEffect(() => {
    const dir = i18n.dir(i18n.language);
    document.documentElement.dir = dir;
  }, [i18n, i18n.language]);

  return null;
};

// --- COMPONENT 1: PROVIDER TREE (Just Wrappers) ---
const AppProviders = ({ children }) => (
  <AnalyticsProvider>
    <UserProvider>
      <GuestUserProvider>
        <SocialAuthProvider>
          <WalletProvider>
            <NavigationProvider>
              <CarouselProvider>
                <LoadingProvider>
                  <ModalProvider>
                    {children}
                  </ModalProvider>
                </LoadingProvider>
              </CarouselProvider>
            </NavigationProvider>
          </WalletProvider>
        </SocialAuthProvider>
      </GuestUserProvider>
    </UserProvider>
  </AnalyticsProvider>
);

// --- COMPONENT 2: APP SHELL (Consumer & Layout) ---
const AppShell = () => {
  // SAFE: We are inside <RouterProvider>, so hooks work.
  const { isBannerActive } = useAnalytics();
  const { isLoading } = useLoading();

  // --- TWA: Dismiss the animated brand loader once the app is mounted ---
  useEffect(() => {
    const loader = document.getElementById('app-shell-loader');
    if (loader) {
      loader.style.opacity = '0';
      setTimeout(() => loader.remove(), 400); // matches CSS transition duration
    }
  }, []);

  return (
    <>
      {isLoading && <ModalLoading />}

      {/* Logic Components */}
      <AppEffects />
      <AnalyticsTracker />
      <ScrollToTop />
      <ExitIntentDetector />

      {/* Main Content */}
      <GlobalErrorBoundary>
        <Suspense fallback={<SuspenseManager />}>
          <Outlet />
        </Suspense>
      </GlobalErrorBoundary>

      {/* Global Overlays */}
      <Suspense fallback={null}>
        <GlobalAiButton />
      </Suspense>

      <div className="fixed bottom-0 left-0 right-0 z-[1000]">
        {isBannerActive && <CookieBanner />}
      </div>

      <ResumePrompt />
      <Toaster position="top-center" />
    </>
  );
};

// --- 3. ROUTER DEFINITION ---
const router = createBrowserRouter(
  createRoutesFromElements(
    // STRUCTURE: Provider Tree -> App Shell (Outlet)
    <Route element={<AppProviders><AppShell /></AppProviders>}>

      {/* Public routes */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Home />} />
        <Route path="home2" element={<Home2 />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="terms-of-service" element={<TermsOfService />} />
        <Route path="cookie-policy" element={<CookiePolicy />} />
        <Route path="redeem" element={<Redeem />} />
        <Route path="faq" element={<FAQ />} />
        <Route path="ambassador/terms" element={<AffiliateTermsPage />} />
        <Route path="storymaker/terms" element={<StorymakerTermsPage />} />
        <Route path="earn" element={<EarnPage />} />
      </Route>

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/action" element={<AuthActionHandler />} />
        <Route path="/claim-account" element={<ClaimAccountPage />} />
      </Route>

      {/* Standalone */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/share/:type/:identifier" element={<PublicSharePage />} />
      <Route path="/r/:username" element={<ReferralRedirect />} />

      {/* Public/Hybrid Dashboard Route */}
      <Route element={<DashboardLayoutWrapper />}>
        <Route path="/library" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="activate" element={<TVActivatePage />} />
        <Route element={<DashboardLayoutWrapper />}>
          <Route path="/settings" element={<Settings />} />
          <Route path="/pass-status" element={<IndikinPassStatus />} />
          <Route path="/ambassador" element={<EarnDashboard />} />
          <Route path="/ambassador/leaderboard" element={<Leaderboard />} />
          <Route path="/dashboard/affiliates" element={<AffiliateAdmin />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Route>
  )
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
